import React, { FC } from 'react'
import classNames from 'classnames'
import { JobPublicReadOneDto } from '@/client-sdk'
import NextImage from 'next/image'
import useTranslation from 'next-translate/useTranslation'
import formatLocation from '@/utils/formatLocation'
import { Wallet, MapPinCheckInside, FileText, Check, X, Rocket } from 'lucide-react'

export interface IJobOfferCard extends JobPublicReadOneDto, React.PropsWithChildren {
	noBorder?: boolean
	clickable?: boolean
}

interface JobOfferCardProps {
	company: {
		logoUrl: string
		name: string
	}
	country: string
	salaryRange: string
	currency: string
	city: string
	contractType: JobPublicReadOneDto.ContractTypeEnum[]
	offerOptions: Record<string, any>
	experienceLevel: string
	positionName: JobPublicReadOneDto.PositionNamesEnum[]
	overtime: boolean
}

export const JobOfferCard: FC<JobOfferCardProps> = ({
	company,
	country,
	salaryRange,
	currency,
	city,
	contractType,
	offerOptions,
	experienceLevel,
	positionName,
	overtime,
}) => {
	const { t } = useTranslation('offer')
	const { t: tCommon } = useTranslation('common')
	const { t: tTalent } = useTranslation('talent')
	const getShortContractTypeTranslation = (
		contractType: JobPublicReadOneDto.ContractTypeEnum,
		t: (key: string) => string,
	): string => {
		if (contractType === JobPublicReadOneDto.ContractTypeEnum.B2b) {
			return t('contract-card.b2b')
		}
		return t('contract-card.employment_contract')
	}

	return (
		<div
			className={classNames(
				'bg-white border border-solid border-brand-gray-200 group shadow-md rounded-xl group relative duration-300 group hover:shadow-2xl hover:border-brand-primary-md flex flex-col h-full overflow-hidden',
			)}
		>
			<div className="group absolute top-0 left-0 w-full h-[6px] bg-brand-primary-md opacity-0 group group-hover:opacity-100 transition-opacity duration-300"></div>

			<div className="p-4 space-y-4 md:p-4 rounded-xl flex-grow">
				<div className="flex flex-col sm:flex-row gap-2 sm:gap-3 min-h-[4rem] px-1 relative">
					<div className="flex justify-between items-center w-full sm:w-auto sticky top-0 bg-white z-10 py-1">
						<NextImage
							width={48}
							height={48}
							src={company?.logoUrl || 'https://i.ibb.co/Rc6D9L1/constraction-znak-cmyk.png'}
							alt={company?.name || 'company-logo'}
							className="max-w-[48px] max-h-[48px] sm:max-w-[60px] sm:max-h-[60px] flex-shrink-0 rounded-md object-contain"
						/>

						<div className="sm:hidden">
							<button className="bg-brand-secondary rounded-md px-3 py-1.5 text-xs text-brand-gray-400 font-lexend font-medium whitespace-nowrap">
								{t('job-board:apply-for-job')}
							</button>
						</div>
					</div>

					<div className="relative w-full flex-grow">
						<div className="flex sm:grid sm:grid-cols-[1fr,auto] gap-2 sm:gap-3 items-start sm:pt-1">
							<div className="min-w-0">
								<div
									className="text-sm sm:text-base font-semibold text-brand-gray-400 line-clamp-2 mb-1"
									title={
										Array.isArray(positionName)
											? positionName.map(pos => tTalent(`positions.${pos}`)).join(', ')
											: tTalent(`positions.${positionName}`)
									}
								>
									{Array.isArray(positionName)
										? positionName.map(pos => tTalent(`positions.${pos}`)).join(', ')
										: tTalent(`positions.${positionName}`)}
								</div>
								<div className="text-xs sm:text-sm font-medium text-brand-gray-300 line-clamp-1" title={company?.name}>
									{company?.name}
								</div>
							</div>

							<div className="hidden sm:block flex-shrink-0 self-start pt-0.5">
								<button className="bg-brand-secondary rounded-md px-3 py-1.5 text-xs text-brand-gray-400 font-lexend font-medium whitespace-nowrap">
									{t('job-board:apply-for-job')}
								</button>
							</div>
						</div>
					</div>
				</div>
				<div className="flex gap-3 md:gap-2 py-1">
					<div className="grid grid-cols-2 gap-y-3 w-full text-opacity-70 text-brand-gray-400">
						<span className="flex items-center gap-2 py-1 md:px-1 rounded-full">
							<Wallet className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium text-brand-gray-400 line-clamp-1">
								{salaryRange ? `${salaryRange} ${currency}` : t('salary.negotiable')}
							</span>
						</span>
						<span className="flex items-center gap-2 py-1 md:px-2 rounded-full">
							<MapPinCheckInside className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium capitalize text-brand-gray-400 line-clamp-1">
								{formatLocation(tCommon(`countries.${country}`), city)}
							</span>
						</span>
						<span className="flex items-center gap-2 py-1 md:px-1 rounded-full">
							<Rocket className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium text-brand-gray-400 line-clamp-1">
								{experienceLevel ? t(`experienceLevel.${experienceLevel}`) : t('experienceLevel.unknown')}
							</span>
						</span>
						<span className="flex items-center gap-2 py-1 md:px-2 rounded-full">
							<FileText className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium text-brand-gray-400 line-clamp-1">
								{Array.isArray(contractType)
									? [
											// @ts-ignore
											...new Set(
												contractType.map(type =>
													getShortContractTypeTranslation(type as JobPublicReadOneDto.ContractTypeEnum, t),
												),
											),
										].join(', ')
									: getShortContractTypeTranslation(contractType as JobPublicReadOneDto.ContractTypeEnum, t)}
							</span>
						</span>
					</div>
				</div>
			</div>

			<div className="mt-auto w-full rounded-b-xl overflow-hidden">
				<div className="bg-gray-100 p-4 md:px-5 space-y-3">
					<div className="flex flex-wrap justify-center sm:flex sm:justify-between items-center gap-4">
						{offerOptions?.ensuredAccommodation !== undefined && (
							<div className="flex items-center gap-2 lg:justify-center">
								{offerOptions.ensuredAccommodation ? (
									<Check className="w-5 h-5 text-brand-primary-md shrink-0" />
								) : (
									<X className="w-5 h-5 text-brand-primary-md shrink-0" />
								)}
								<span className="text-xs font-medium text-brand-gray-400">{t('provided.accommodation-short')}</span>
							</div>
						)}
						{offerOptions?.ensuredTransport !== undefined && (
							<div className="flex items-center gap-2 lg:justify-center">
								{offerOptions.ensuredTransport ? (
									<Check className="w-5 h-5 text-brand-primary-md shrink-0" />
								) : (
									<X className="w-5 h-5 text-brand-primary-md shrink-0" />
								)}
								<span className="text-xs font-medium text-brand-gray-400">{t('provided.transportation-short')}</span>
							</div>
						)}

						{overtime !== undefined && (
							<div className="flex items-center gap-2 lg:justify-center">
								{overtime ? (
									<Check className="w-5 h-5 text-brand-primary-md shrink-0" />
								) : (
									<X className="w-5 h-5 text-brand-primary-md shrink-0" />
								)}
								<span className="text-xs font-medium text-brand-gray-400">{t('provided.overtime-short')}</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

JobOfferCard.displayName = 'JobOfferCard'
